import { useBridgeContext } from '@sendible/shared-state-bridge';
import { useQueryWithAccessToken } from '../../data-layer/useReactQueryWithAccessToken';
import endpoints from '../../data-layer/endpoints';

type QueueFilterItem = Omit<FilterItem, 'id'> & {
  id: string | number;
  paused?: boolean;
};

type FiltersResponse = FilterData & {
  networks?: FilterItem[];
  queues?: QueueFilterItem[];
};

const { GetFilters } = endpoints;

const getFiltersEndpoint = (isWorkspaceMode: boolean) => (isWorkspaceMode ? GetFilters.endpoint.replace('/v1.0/', '/v2.0/') : GetFilters.endpoint);
const getDashboardParams = (userId: number | undefined, isWorkspaceMode: boolean) => (!isWorkspaceMode && userId ? { dashboardId: userId } : {});

const transformFilterData = (data: FilterData): FilterData => {
  const { networks: networksRaw, queues: queuesRaw } = data as FiltersResponse;

  const networks = (networksRaw ?? []).map((n) => {
    if (n.id === 'GoogleBusiness') return { ...n, label: 'Google Business' };

    return n;
  });

  const queues = (queuesRaw ?? []).map((q) => {
    const paused = q.metadata?.isPaused === true;
    const workspace = q.metadata?.workspace as { name?: string } | undefined;
    const label = (q.label === 'My Queue' || q.label === 'Default') && workspace?.name ? `${workspace.name}'s Queue` : q.label;
    const subLabel = workspace?.name || undefined;

    return {
      ...q,
      label,
      subLabel,
      paused,
    };
  });

  return {
    ...(data as FiltersResponse),
    networks,
    queues,
  };
};

export const useGetFilters = (filters: Filters[]) => {
  const [{ user }] = useBridgeContext();
  const { userId } = user || {};
  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);
  const endpoint = getFiltersEndpoint(isWorkspaceMode);
  const workspaceParams = isWorkspaceMode && userId ? { workspaceId: userId } : {};

  return useQueryWithAccessToken<FilterData>(
    [
      `${endpoint}`,
      {
        filterTypes: filters.join(','),
        ...getDashboardParams(userId, isWorkspaceMode),
        ...workspaceParams,
      },
    ],
    {
      enabled: !!filters,
      staleTime: Infinity,
      cacheTime: 0,
      select: (data) => transformFilterData(data),
    }
  );
};
